import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Boxes, PackageOpen, RefreshCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { fetchRetailCatalog, fetchRetailInventory } from "@/lib/api/retail";
import type {
  InventoryTransferSuggestion,
  RetailInventoryLevel,
  RetailProduct,
} from "@/features/retail/types/datasets";
import RetailCycleCountDialog from "@/features/retail/components/RetailCycleCountDialog";
import RetailReceiveStockDialog from "@/features/retail/components/RetailReceiveStockDialog";
import { useSocket } from "@/hooks/useSocket";

type InventoryRow = {
  productId: string;
  productName: string;
  sku: string;
  category: string;
  levelId: string | null;
  locationId: string | null;
  locationName: string;
  supplierName: string | null;
  tags: string[];
  available: number;
  onOrder: number;
  reorderPoint: number | null;
  safetyStock: number | null;
  status: "healthy" | "low" | "out";
  nextDeliveryEta: string | null;
  lastReceivedAt: string | null;
  nextDeliveryLabel: string;
  lastReceivedLabel: string;
  transferSuggestions: InventoryTransferSuggestion[];
};

type TranslateFn = TFunction<"retail" | "common">;

const statusBadgeTone: Record<InventoryRow["status"], string> = {
  healthy:
    "border border-emerald-200/70 bg-emerald-100/60 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/15 dark:text-emerald-200",
  low: "border border-amber-200/70 bg-amber-100/60 text-amber-700 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-200",
  out: "border border-rose-200/70 bg-rose-100/60 text-rose-700 dark:border-rose-500/35 dark:bg-rose-500/15 dark:text-rose-200",
};

const formatRelativeDate = (value: string | null, translate: TranslateFn) => {
  if (!value) {
    return translate("retail.inventory.table.notAvailable", { defaultValue: "-" });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return translate("retail.inventory.table.notAvailable", { defaultValue: "-" });
  }
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));

  if (Math.abs(diffHours) < 1) {
    return translate("retail.inventory.table.relative.now", { defaultValue: "Now" });
  }

  if (diffHours > 0) {
    if (diffHours < 24) {
      return translate("retail.inventory.table.relative.inHours", {
        count: diffHours,
        defaultValue: "In {{count}} hours",
      });
    }
    const diffDays = Math.round(diffHours / 24);
    return translate("retail.inventory.table.relative.inDays", {
      count: diffDays,
      defaultValue: "In {{count}} days",
    });
  }

  const hoursAgo = Math.abs(diffHours);
  if (hoursAgo < 24) {
    return translate("retail.inventory.table.relative.hoursAgo", {
      count: hoursAgo,
      defaultValue: "{{count}} hours ago",
    });
  }
  const daysAgo = Math.round(hoursAgo / 24);
  return translate("retail.inventory.table.relative.daysAgo", {
    count: daysAgo,
    defaultValue: "{{count}} days ago",
  });
};

const deriveStatus = (product: RetailProduct, available: number): InventoryRow["status"] => {
  if (!Number.isFinite(available) || available <= 0) {
    return "out";
  }
  if (product.reorderPoint !== null && available <= product.reorderPoint) {
    return "low";
  }
  if (product.safetyStock !== null && available <= product.safetyStock) {
    return "low";
  }
  return "healthy";
};

const buildInventoryRows = (
  products: RetailProduct[],
  inventoryLevels: RetailInventoryLevel[],
  translate: TranslateFn,
): InventoryRow[] => {
  if (products.length === 0 || inventoryLevels.length === 0) {
    return [];
  }

  const levelsByProduct = new Map<string, RetailInventoryLevel[]>();
  for (const level of inventoryLevels) {
    const productId = level.productId;
    if (!productId) {
      continue;
    }
    const existing = levelsByProduct.get(productId);
    if (existing) {
      existing.push(level);
    } else {
      levelsByProduct.set(productId, [level]);
    }
  }

  const rows: InventoryRow[] = [];

  for (const product of products) {
    const related = levelsByProduct.get(product.id);
    if (!related || related.length === 0) {
      continue;
    }

    let available = 0;
    let onOrder = 0;
    const nextDeliveryCandidates: string[] = [];
    const lastReceivedCandidates: string[] = [];
    const suggestionMap = new Map<string, InventoryTransferSuggestion>();

    for (const level of related) {
      available += Math.max(0, level.onHand - level.allocated);
      onOrder += level.onOrder;
      if (typeof level.nextDeliveryEta === "string" && level.nextDeliveryEta.length > 0) {
        nextDeliveryCandidates.push(level.nextDeliveryEta);
      }
      if (typeof level.lastReceivedAt === "string" && level.lastReceivedAt.length > 0) {
        lastReceivedCandidates.push(level.lastReceivedAt);
      }
      const suggestions = Array.isArray(level.metadata?.transferSuggestions)
        ? (level.metadata?.transferSuggestions as InventoryTransferSuggestion[])
        : [];
      for (const suggestion of suggestions) {
        if (!suggestion?.id) {
          continue;
        }
        suggestionMap.set(suggestion.id, suggestion);
      }
    }

    nextDeliveryCandidates.sort();
    lastReceivedCandidates.sort();

    const primaryLevel = related[0] ?? null;
    const nextDeliveryEta = nextDeliveryCandidates[0] ?? null;
    const lastReceivedAt =
      lastReceivedCandidates[lastReceivedCandidates.length - 1] ?? null;

    rows.push({
      productId: product.id,
      productName: product.name,
      sku: product.sku,
      category: product.category,
      levelId: primaryLevel?.id ?? null,
      locationId: primaryLevel?.locationId ?? null,
      locationName: primaryLevel?.locationName?.trim() ?? "",
      supplierName: product.supplierName ?? null,
      tags: product.tags,
      available,
      onOrder,
      reorderPoint: product.reorderPoint,
      safetyStock: product.safetyStock,
      status: deriveStatus(product, available),
      nextDeliveryEta,
      lastReceivedAt,
      nextDeliveryLabel: formatRelativeDate(nextDeliveryEta, translate),
      lastReceivedLabel: formatRelativeDate(lastReceivedAt, translate),
      transferSuggestions: Array.from(suggestionMap.values()),
    });
  }

  return rows;
};

type InventoryDeltaEvent = {
  levelId: string;
  productId?: string | null;
  locationId?: string | null;
  quantityDelta: number;
  onHand: number;
  eventType: string;
  ledgerId: string;
  occurredAt: string;
};

export const RetailInventoryView = () => {
  const { t } = useTranslation(["common", "retail"]);
  const { toast } = useToast();
  const { formatNumber } = useLocaleFormatter();
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { socket, connected } = useSocket();

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
  });

  const inventoryQuery = useQuery({
    queryKey: ["retail", "inventory"],
    queryFn: ({ signal }) => fetchRetailInventory({ signal }),
  });

  const isLoading = catalogQuery.isLoading || inventoryQuery.isLoading;
  const isError = catalogQuery.isError || inventoryQuery.isError;

  const rows = useMemo<InventoryRow[]>(
    () =>
      buildInventoryRows(
        catalogQuery.data ?? [],
        inventoryQuery.data ?? [],
        t,
      ),
    [catalogQuery.data, inventoryQuery.data, t],
  );

  // Note: Disable fixed-height virtualization.
  // Rows vary in height due to wrapping text and badges across locales/screen sizes.
  // Virtualization with a hardcoded row height caused overlap and gaps. Rendering all rows avoids those issues.

  useEffect(() => {
    if (!socket || !connected) {
      return;
    }

    const handleInventoryDelta = (payload: InventoryDeltaEvent) => {
      if (!payload || typeof payload.levelId !== "string") {
        return;
      }

      queryClient.setQueryData<RetailInventoryLevel[] | undefined>(
        ["retail", "inventory"],
        (previous) => {
          if (!Array.isArray(previous)) {
            return previous;
          }
          let changed = false;
          const updated = previous.map((level) => {
            if (level.id !== payload.levelId) {
              return level;
            }
            changed = true;
            const occurredAtIso = payload.occurredAt ?? new Date().toISOString();
            const nextLevel: RetailInventoryLevel = {
              ...level,
              onHand: payload.onHand,
              updatedAt: occurredAtIso,
            };
            if (payload.eventType === "receipt") {
              nextLevel.lastReceivedAt = occurredAtIso;
            }
            return nextLevel;
          });
          return changed ? updated : previous;
        },
      );
    };

    socket.on("retail.inventory.delta", handleInventoryDelta);
    return () => {
      socket.off("retail.inventory.delta", handleInventoryDelta);
    };
  }, [socket, connected, queryClient]);

  // Former virtualization code removed to ensure correctness with variable-height rows.

  const lowStockCount = useMemo(
    () => rows.filter((row) => row.status === "low").length,
    [rows],
  );

  const outOfStockCount = useMemo(
    () => rows.filter((row) => row.status === "out").length,
    [rows],
  );

  const transferSuggestions = useMemo(() => {
    const defaultSourceLabel = t("retail.inventory.suggestions.defaultSource", {
      defaultValue: "Commissary",
    });
    const suggestions: Array<
      InventoryTransferSuggestion & { targetProductName: string; targetLocationName: string }
    > = [];
    for (const row of rows) {
      for (const suggestion of row.transferSuggestions) {
        suggestions.push({
          ...suggestion,
          targetProductName: suggestion.productName || row.productName,
          targetLocationName:
            suggestion.targetLocationName ||
            row.locationName ||
            t("retail.inventory.table.notAvailable", { defaultValue: "-" }),
          sourceLocationName:
            suggestion.sourceLocationName || defaultSourceLabel,
        });
      }
    }
    return suggestions.sort((left, right) => {
      const leftDate = new Date(left.generatedAt).getTime();
      const rightDate = new Date(right.generatedAt).getTime();
      return rightDate - leftDate;
    });
  }, [rows, t]);

  const activeSkus = rows.length;

  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveProductId, setReceiveProductId] = useState<string | null>(null);
  const [receiveLevelId, setReceiveLevelId] = useState<string | null>(null);

  const handleReceive = (row: InventoryRow) => {
    if (!row.levelId) {
      toast({
        title: t("retail.inventory.toast.missingLevel", { defaultValue: "Inventory level unavailable" }),
        description: t("retail.inventory.toast.missingLevelDescription", {
          defaultValue: "Refresh and try again.",
        }),
        variant: "destructive",
      });
      return;
    }

    setReceiveProductId(row.productId ?? null);
    setReceiveLevelId(row.levelId);
    setReceiveDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Badge className="border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-200">
            {t("retail.inventory.previewBadge", { defaultValue: "Retail preview" })}
          </Badge>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {t("retail.inventory.title", { defaultValue: "Inventory overview" })}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("retail.inventory.subtitle", {
              defaultValue: "Track stock positions, inbound shipments, and replenishment priorities across the storefront.",
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setCycleDialogOpen(true)}
          >
            <RefreshCcw className="h-4 w-4" />
            {t("retail.inventory.actions.recount", { defaultValue: "Start cycle count" })}
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setReceiveProductId(null);
              setReceiveLevelId(null);
              setReceiveDialogOpen(true);
            }}
          >
            <PackageOpen className="h-4 w-4" />
            {t("retail.inventory.actions.receiveStock", { defaultValue: "Receive stock" })}
          </Button>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t("retail.inventory.summary.activeSkus.label", { defaultValue: "Active SKUs" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
              {formatNumber(activeSkus)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.summary.activeSkus.description", {
                defaultValue: "Available for sale in this location.",
              })}
            </p>
          </CardHeader>
        </Card>
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-300">
              {t("retail.inventory.summary.lowStock.label", { defaultValue: "Low stock" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-amber-600 dark:text-amber-300">
              {formatNumber(lowStockCount)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.summary.lowStock.description", {
                defaultValue: "Below reorder threshold. Prioritize replenishment.",
              })}
            </p>
          </CardHeader>
        </Card>
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="text-xs uppercase tracking-wide text-rose-600 dark:text-rose-300">
              {t("retail.inventory.summary.outStock.label", { defaultValue: "Out of stock" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-rose-600 dark:text-rose-300">
              {formatNumber(outOfStockCount)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.summary.outStock.description", {
                defaultValue: "Unavailable on shelf. Communicate to staff.",
              })}
            </p>
          </CardHeader>
        </Card>
      </section>

      {transferSuggestions.length > 0 ? (
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {t("retail.inventory.suggestions.title", { defaultValue: "Recommended transfers" })}
            </CardTitle>
            <CardDescription className="text-sm text-slate-500 dark:text-slate-400">
              {t("retail.inventory.suggestions.subtitle", {
                defaultValue: "Pull stock from commissary levels to restore par before service.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {transferSuggestions.slice(0, 3).map((suggestion) => (
              <div
                key={suggestion.id}
                className="flex items-start justify-between gap-3 rounded-md border border-slate-200/70 bg-white/85 p-3 dark:border-slate-800/60 dark:bg-slate-950/60"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {suggestion.targetProductName}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t("retail.inventory.suggestions.route", {
                      defaultValue: "{{from}} → {{to}}",
                      from:
                        suggestion.sourceLocationName ??
                        t("retail.inventory.suggestions.defaultSource", { defaultValue: "Commissary" }),
                      to:
                        suggestion.targetLocationName ??
                        t("retail.inventory.suggestions.defaultTarget", { defaultValue: "Destination" }),
                    })}
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0 text-xs">
                  {t("retail.inventory.suggestions.quantity", {
                    defaultValue: "{{count}} units",
                    count: suggestion.quantity,
                  })}
                </Badge>
              </div>
            ))}
            {transferSuggestions.length > 3 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("retail.inventory.suggestions.more", {
                  defaultValue: "{{count}} additional suggestions available.",
                  count: transferSuggestions.length - 3,
                })}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("retail.inventory.errors.title", { defaultValue: "Unable to load inventory" })}</AlertTitle>
          <AlertDescription>
            {t("retail.inventory.errors.description", {
              defaultValue: "The storefront data feed is unavailable. Try again shortly.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              {t("retail.inventory.table.title", { defaultValue: "Store stock positions" })}
            </CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">
              {t("retail.inventory.table.subtitle", {
                defaultValue: "Spot variances, inbound shipments, and reorder opportunities at a glance.",
              })}
            </CardDescription>
          </div>
          <Badge variant="outline" className="gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-300">
            <Boxes className="h-4 w-4 text-emerald-500 dark:text-emerald-300" />
            {t("retail.inventory.table.badge", { defaultValue: "Storefront data" })}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton
                  key={`inventory-row-skeleton-${index}`}
                  className="h-12 rounded-xl bg-slate-200/60 dark:bg-slate-800/60"
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200/70 bg-white/95 shadow-sm dark:border-slate-800/60 dark:bg-slate-950/65">
              <div className="-mx-4 overflow-x-auto sm:mx-0">
                <Table className="min-w-[640px] md:min-w-[720px]">
                  <TableHeader>
                    <TableRow className="border-slate-200/70 dark:border-slate-800/70">
                      <TableHead className="w-[28%]">{t("retail.inventory.table.columns.product", { defaultValue: "Product" })}</TableHead>
                      <TableHead>{t("retail.inventory.table.columns.status", { defaultValue: "Status" })}</TableHead>
                      <TableHead className="text-right">{t("retail.inventory.table.columns.available", { defaultValue: "Available" })}</TableHead>
                    <TableHead className="text-right">{t("retail.inventory.table.columns.onOrder", { defaultValue: "On order" })}</TableHead>
                    <TableHead>{t("retail.inventory.table.columns.nextDelivery", { defaultValue: "Next delivery" })}</TableHead>
                    <TableHead>{t("retail.inventory.table.columns.lastReceived", { defaultValue: "Last received" })}</TableHead>
                    <TableHead className="text-right">{t("retail.inventory.table.columns.actions", { defaultValue: "Actions" })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length > 0 ? (
                      rows.map((row) => (
                        <TableRow key={row.productId} className="border-slate-200/70 transition-colors hover:bg-emerald-50/40 dark:border-slate-800/70 dark:hover:bg-emerald-500/10">
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-slate-900 dark:text-slate-100">{row.productName}</span>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span>{row.sku}</span>
                            <span>•</span>
                            <span>{row.category}</span>
                          {row.supplierName ? (
                            <>
                              <span>•</span>
                              <span>
                                {t("retail.inventory.table.labels.supplier", { defaultValue: "Supplier" })}: {row.supplierName}
                              </span>
                            </>
                          ) : null}
                          {row.locationName ? (
                            <>
                              <span>•</span>
                              <span>{row.locationName}</span>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                      <TableCell>
                        <Badge className={cn("rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide", statusBadgeTone[row.status])}>
                          {row.status === "healthy"
                            ? t("retail.inventory.table.status.healthy", { defaultValue: "Healthy" })
                            : row.status === "low"
                            ? t("retail.inventory.table.status.low", { defaultValue: "Low" })
                            : t("retail.inventory.table.status.out", { defaultValue: "Out" })}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900 dark:text-slate-100">
                        {formatNumber(row.available)}
                      </TableCell>
                      <TableCell className="text-right text-slate-600 dark:text-slate-300">
                        {row.onOrder > 0
                          ? formatNumber(row.onOrder)
                          : t("retail.inventory.table.notAvailable", { defaultValue: "-" })}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-300">{row.nextDeliveryLabel}</TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-300">{row.lastReceivedLabel}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReceive(row)}
                          disabled={!row.levelId}
                        >
                          {t("retail.inventory.table.receiveQuick", { defaultValue: "Receive stock" })}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-slate-500 dark:text-slate-400">
                          {t("retail.inventory.table.empty", { defaultValue: "No inventory records to display yet." })}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <RetailReceiveStockDialog
        open={receiveDialogOpen}
        onOpenChange={setReceiveDialogOpen}
        defaultProductId={receiveProductId}
        defaultLevelId={receiveLevelId}
      />
      <RetailCycleCountDialog
        open={cycleDialogOpen}
        onOpenChange={setCycleDialogOpen}
      />
    </div>
  );
};

export default RetailInventoryView;
